import * as React from 'react';
import { useQuery } from 'react-query';
import IMembersPortalComponentDataItem from '../../../../redux/types/IMembersPortalComponentDataItem';
import { MembersPortalService } from '../../../../redux/services/MembersPortalService';
import {
    IMemberStatementPaymentSummaryDetails,
    IMemberStatementSearchResult
} from '../../../types/IMemberStatementSearchResult';
import { SectionedTable, SectionedTableType } from '../../../sharedComponents/SectionedTable/SectionedTable';
import { hasAnyValidValue } from '../../../../util/tools';
import { MEMBER_STATEMENT_PAYMENT_SUMMARY_KEY, MEMBER_STATEMENT_DESCRIPTION_KEY, MEMBER_STATEMENT_MEMBER_NAME_KEY, MEMBER_STATEMENT_MEMBER_NUMBER_KEY, MEMBER_STATEMENT_IPI_NUMBER_KEY, MEMBER_STATEMENT_PAYMENT_DATE_KEY, MEMBER_STATEMENT_PAYMENT_RUN_NARRATIVE_KEY, MEMBER_STATEMENT_PAYMENT_ID_KEY } from '../../../Consts';

export interface IMemberStatementPaymentSummaryGridProps {
    payment?: IMemberStatementSearchResult;
    paymentRunID: number;
    refreshKey?: number;
    paymentSummaryGridsComponentData: IMembersPortalComponentDataItem;
}

export const MemberStatementPaymentSummaryGrid: React.FC<IMemberStatementPaymentSummaryGridProps> = (props) => {
    const [summaryViewData, setSummaryViewData] = React.useState<Record<string, string>>({});
    const [loaded, setLoaded] = React.useState(false);

    const currentViewAsUserName = sessionStorage.getItem('currentViewAsUserName');
    const currentUserNameNumber = sessionStorage.getItem('currentUserNameNumber');

    const {
        data,
        isLoading
    } = useQuery<IMemberStatementPaymentSummaryDetails>({
        queryKey: [
            'statement-payment-summary',
            props.paymentRunID,
            props.refreshKey
        ],
        queryFn: () =>
            MembersPortalService.getStatementPaymentSummary(
                props.paymentRunID
            ),
        refetchOnWindowFocus: false,
        staleTime: Infinity
    });

    React.useEffect(() => {
        const {
            paymentSummaryGridsComponentData: { fields }
        } = props;

        let viewData = {};

        if (fields) {
            const visibleFields = fields.filter(field => !field.hidden);

            visibleFields.forEach(item => {
                viewData[item.name] = item.data;

                if (Object.keys(viewData).length === visibleFields.length) {
                    setSummaryViewData(viewData);
                    setLoaded(true);
                }
            });
        }
    }, [props.paymentSummaryGridsComponentData]);

    if (!loaded || isLoading) return <div>Loading...</div>;

    if (!data) return <div></div>;

    return (
        <div className="memberStatementPaymentSummaryGrid">
            <div className="memberStatementPaymentSummaryCard">
                <div>
                    <span className="subtitle">{summaryViewData[MEMBER_STATEMENT_PAYMENT_SUMMARY_KEY]}</span>
                </div>

                <div className="memberStatementPaymentSummaryGridHeader">
                    <div className="memberStatementPaymentSummaryGridHeaderRow">
                        <div className="memberStatementPaymentSummaryGridHeaderItem">
                            <span className="memberStatementPaymentSummaryGridHeaderLabel">
                                {summaryViewData[MEMBER_STATEMENT_DESCRIPTION_KEY]}:
                            </span>
                            <span>{props.payment?.description ?? "-"}</span>
                        </div>

                        <div className="memberStatementPaymentSummaryGridHeaderItem">
                            <span className="memberStatementPaymentSummaryGridHeaderLabel">
                                {summaryViewData[MEMBER_STATEMENT_MEMBER_NAME_KEY]}:
                            </span>
                            <span>{currentViewAsUserName ?? "-"}</span>
                        </div>

                        <div className="memberStatementPaymentSummaryGridHeaderItem">
                            <span className="memberStatementPaymentSummaryGridHeaderLabel">
                                {summaryViewData[MEMBER_STATEMENT_MEMBER_NUMBER_KEY]}:
                            </span>
                            <span>{data.payeeMemberNumber ?? "-"}</span>
                        </div>

                        {hasAnyValidValue(currentUserNameNumber) && (
                            <div className="memberStatementPaymentSummaryGridHeaderItem">
                                <span className="memberStatementPaymentSummaryGridHeaderLabel">
                                    {summaryViewData[MEMBER_STATEMENT_IPI_NUMBER_KEY]}:
                                </span>
                                <span>{currentUserNameNumber}</span>
                            </div>
                        )}
                    </div>

                    <div className="memberStatementPaymentSummaryGridHeaderRow memberStatementPaymentSummaryGridHeaderRowSingle">
                        <div className="memberStatementPaymentSummaryGridHeaderItem">
                            <span className="memberStatementPaymentSummaryGridHeaderLabel">
                                {summaryViewData[MEMBER_STATEMENT_PAYMENT_DATE_KEY]}:
                            </span>
                            <span> {(props.payment as any)?.paymentRunDateDisplay ?? (props.payment as any)?.paymentRunDate ?? props.payment?.paymentDate ?? "-"}</span>
                        </div>
                    </div>
                </div>

                <div className="memberStatementPaymentSummaryTableBox">
                    <SectionedTable
                        data={data}
                        viewData={summaryViewData}
                        type={SectionedTableType.Currency}
                        excludedKeys={[
                            MEMBER_STATEMENT_PAYMENT_SUMMARY_KEY,
                            MEMBER_STATEMENT_DESCRIPTION_KEY,
                            MEMBER_STATEMENT_MEMBER_NAME_KEY,
                            MEMBER_STATEMENT_MEMBER_NUMBER_KEY,
                            MEMBER_STATEMENT_IPI_NUMBER_KEY,
                            MEMBER_STATEMENT_PAYMENT_DATE_KEY,
                            MEMBER_STATEMENT_PAYMENT_ID_KEY,
                            MEMBER_STATEMENT_PAYMENT_RUN_NARRATIVE_KEY
                        ]}
                    />
                </div>
            </div>
        </div>
    );
};

export default MemberStatementPaymentSummaryGrid;